/**
 * Related registry documents — hyperlinked doc graph for same project/row
 */

import React from 'react';
import { Badge } from '../UI/Badge';
import { Button } from '../UI/Button';
import { Card } from '../UI/Card';
import { ExternalLink, FileText, Link2, Network } from 'lucide-react';
import {
  extractRegistryMeta,
  formatDocTypeLabel,
  formatRegistryColumnLabel,
  getRelatedRegistryArtifacts,
  isExternalRegistryHref,
} from '../../utils/registryArtifactUtils';
import type { Artifact } from '../../types/api';

interface RelatedRegistryArtifactsProps {
  artifact: Artifact;
  allArtifacts: Artifact[];
  onSelectArtifact: (artifactId: string) => void;
}

export function RelatedRegistryArtifacts({
  artifact,
  allArtifacts,
  onSelectArtifact,
}: RelatedRegistryArtifactsProps) {
  const reg = extractRegistryMeta(artifact);
  const related = getRelatedRegistryArtifacts(artifact, allArtifacts);

  if (!reg?.project_id) return null;

  return (
    <Card className="mt-3 border border-indigo-200 bg-gradient-to-r from-indigo-50 to-violet-50 p-4">
      <div className="mb-3 flex items-center gap-2">
        <Network className="h-4 w-4 text-indigo-600" />
        <h4 className="text-sm font-semibold text-indigo-900">Registry document graph</h4>
        <Badge variant="info" size="sm">
          {reg.project_id}
        </Badge>
        {reg.registry_column && (
          <Badge variant="default" size="sm">
            {formatRegistryColumnLabel(reg.registry_column)}
          </Badge>
        )}
      </div>

      <div className="mb-3 text-xs text-indigo-700">
        {related.length > 0
          ? `${related.length + 1} linked document${related.length === 0 ? '' : 's'} on this registry row`
          : 'No sibling documents from this registry row in the loaded artifact set'}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="primary"
          className="pointer-events-none"
          aria-current="true"
        >
          <FileText className="mr-1 h-3 w-3" />
          {formatDocTypeLabel(reg.doc_type)} (current)
        </Button>

        {related.map((relatedArtifact) => {
          const relatedReg = extractRegistryMeta(relatedArtifact);
          return (
            <Button
              key={relatedArtifact.id}
              size="sm"
              variant="outline"
              onClick={() => onSelectArtifact(relatedArtifact.id)}
              title={relatedArtifact.filename}
            >
              <Link2 className="mr-1 h-3 w-3" />
              {formatDocTypeLabel(relatedReg?.doc_type)}
              <span className="ml-1 max-w-[120px] truncate text-xs opacity-70">
                {relatedArtifact.filename}
              </span>
            </Button>
          );
        })}
      </div>

      {reg.registry_href && (
        <div className="mt-3 space-y-1 text-xs text-indigo-700">
          {isExternalRegistryHref(reg.registry_href) ? (
            <a
              href={reg.registry_href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-primary-600 hover:underline"
            >
              <ExternalLink className="mr-1 h-3 w-3" />
              Open registry source ({formatRegistryColumnLabel(reg.registry_column)})
            </a>
          ) : (
            <div className="truncate font-mono" title={reg.registry_href}>
              Registry href: {reg.registry_href}
            </div>
          )}
          {reg.registry_resolved_path && (
            <div className="truncate font-mono text-indigo-600" title={reg.registry_resolved_path}>
              Resolved path: {reg.registry_resolved_path}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
